import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { EfficiencyOptimization } from '@coffeeplexity/shared';
import { ProgressCircle } from './ProgressCircle';

// const { width } = Dimensions.get('window');

interface EfficiencyOptimizationViewProps {
  optimization: EfficiencyOptimization;
}

export const EfficiencyOptimizationView: React.FC<
  EfficiencyOptimizationViewProps
> = ({ optimization }) => {
  const [activeTab, setActiveTab] = useState<
    'overview' | 'opportunities' | 'habits' | 'schedule' | 'plan'
  >('overview');

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabScrollContent}
      >
        {[
          { id: 'overview', title: 'Overview', icon: 'speedometer-outline' },
          {
            id: 'opportunities',
            title: 'Opportunities',
            icon: 'trending-up-outline',
          },
          { id: 'habits', title: 'Habits', icon: 'refresh-outline' },
          { id: 'schedule', title: 'Schedule', icon: 'time-outline' },
          { id: 'plan', title: 'Plan', icon: 'map-outline' },
        ].map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.activeTab]}
            onPress={() => setActiveTab(tab.id as any)}
          >
            <Ionicons
              name={tab.icon as any}
              size={16}
              color={activeTab === tab.id ? '#6750A4' : '#666'}
            />
            <Text
              style={[
                styles.tabText,
                activeTab === tab.id && styles.activeTabText,
              ]}
            >
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderOverview = () => (
    <View style={styles.overviewContainer}>
      <View style={styles.efficiencyCard}>
        <View style={styles.efficiencyHeader}>
          <Text style={styles.cardTitle}>Learning Efficiency</Text>
          <View style={styles.potentialGain}>
            <Text style={styles.potentialGainValue}>
              +
              {optimization.potentialEfficiency -
                optimization.currentEfficiency}
              %
            </Text>
            <Text style={styles.potentialGainLabel}>Potential Gain</Text>
          </View>
        </View>

        <View style={styles.efficiencyComparison}>
          <View style={styles.efficiencyCircleContainer}>
            <ProgressCircle
              progress={optimization.currentEfficiency}
              size={100}
              strokeWidth={8}
              color='#6750A4'
            />
            <View style={styles.efficiencyCenter}>
              <Text style={styles.efficiencyValue}>
                {optimization.currentEfficiency}%
              </Text>
              <Text style={styles.efficiencyLabel}>Current</Text>
            </View>
          </View>

          <Ionicons name='arrow-forward' size={24} color='#9CA3AF' />

          <View style={styles.efficiencyCircleContainer}>
            <ProgressCircle
              progress={optimization.potentialEfficiency}
              size={100}
              strokeWidth={8}
              color='#10B981'
            />
            <View style={styles.efficiencyCenter}>
              <Text style={styles.efficiencyValue}>
                {optimization.potentialEfficiency}%
              </Text>
              <Text style={styles.efficiencyLabel}>Potential</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.learningStyleCard}>
        <Text style={styles.cardTitle}>Learning Style Alignment</Text>
        <View style={styles.learningStyleHeader}>
          <View style={styles.learningStyleInfo}>
            <Text style={styles.primaryStyle}>
              Primary: {optimization.learningStyleAlignment.primaryStyle}
            </Text>
            {optimization.learningStyleAlignment.secondaryStyle && (
              <Text style={styles.secondaryStyle}>
                Secondary: {optimization.learningStyleAlignment.secondaryStyle}
              </Text>
            )}
          </View>
          <View style={styles.alignmentScore}>
            <Text style={styles.alignmentValue}>
              {optimization.learningStyleAlignment.alignmentScore}%
            </Text>
            <Text style={styles.alignmentLabel}>Aligned</Text>
          </View>
        </View>

        <View style={styles.alignmentBar}>
          <View
            style={[
              styles.alignmentFill,
              {
                width: `${optimization.learningStyleAlignment.alignmentScore}%`,
              },
            ]}
          />
        </View>

        <Text style={styles.contentAlignment}>
          Current content alignment:{' '}
          {optimization.learningStyleAlignment.currentContentAlignment}%
        </Text>
      </View>

      <View style={styles.quickStats}>
        <View style={styles.statCard}>
          <Ionicons name='bulb-outline' size={24} color='#F59E0B' />
          <Text style={styles.statValue}>
            {optimization.improvementOpportunities.length}
          </Text>
          <Text style={styles.statLabel}>Opportunities</Text>
        </View>
        <View style={styles.statCard}>
          <Ionicons name='refresh-outline' size={24} color='#6750A4' />
          <Text style={styles.statValue}>
            {optimization.habitRecommendations.length}
          </Text>
          <Text style={styles.statLabel}>Habit Suggestions</Text>
        </View>
        <View style={styles.statCard}>
          <Ionicons name='checkmark-circle-outline' size={24} color='#10B981' />
          <Text style={styles.statValue}>
            {optimization.scheduleOptimization.adherenceRate}%
          </Text>
          <Text style={styles.statLabel}>Adherence Rate</Text>
        </View>
      </View>
    </View>
  );

  const renderOpportunities = () => (
    <View style={styles.opportunitiesContainer}>
      {optimization.improvementOpportunities.map(opportunity => (
        <View key={opportunity.opportunityId} style={styles.opportunityCard}>
          <View style={styles.opportunityHeader}>
            <Text style={styles.opportunityArea}>{opportunity.area}</Text>
            <View style={styles.potentialGainBadge}>
              <Text style={styles.potentialGainText}>
                +{opportunity.potentialGain}%
              </Text>
            </View>
          </View>

          <View style={styles.opportunityStates}>
            <View style={styles.stateContainer}>
              <Text style={styles.stateLabel}>Current State</Text>
              <Text style={styles.stateValue}>{opportunity.currentState}</Text>
            </View>
            <Ionicons name='arrow-forward' size={16} color='#9CA3AF' />
            <View style={styles.stateContainer}>
              <Text style={styles.stateLabel}>Optimized State</Text>
              <Text style={styles.stateValue}>
                {opportunity.optimizedState}
              </Text>
            </View>
          </View>

          <View style={styles.opportunityMetrics}>
            <View style={styles.metric}>
              <Text style={styles.metricLabel}>Implementation</Text>
              <Text
                style={[
                  styles.metricValue,
                  {
                    color: getDifficultyColor(
                      opportunity.implementationDifficulty
                    ),
                  },
                ]}
              >
                {opportunity.implementationDifficulty}
              </Text>
            </View>
            <View style={styles.metric}>
              <Text style={styles.metricLabel}>Time to Implement</Text>
              <Text style={styles.metricValue}>
                {opportunity.timeToImplement} days
              </Text>
            </View>
          </View>

          {opportunity.resources.length > 0 && (
            <View style={styles.resources}>
              <Text style={styles.resourcesTitle}>Resources Needed:</Text>
              {opportunity.resources.map((resource, index) => (
                <Text key={index} style={styles.resource}>
                  • {resource}
                </Text>
              ))}
            </View>
          )}
        </View>
      ))}
    </View>
  );

  const renderHabits = () => (
    <View style={styles.habitsContainer}>
      {optimization.habitRecommendations.map(habit => (
        <View key={habit.habitId} style={styles.habitCard}>
          <View style={styles.habitHeader}>
            <View style={styles.habitInfo}>
              <Text style={styles.habitName}>{habit.habitName}</Text>
              <View style={styles.habitMeta}>
                <View
                  style={[
                    styles.categoryBadge,
                    { backgroundColor: getCategoryColor(habit.category) },
                  ]}
                >
                  <Text style={styles.categoryText}>{habit.category}</Text>
                </View>
                <Text style={styles.habitFrequency}>{habit.frequency}</Text>
                <Text style={styles.habitDuration}>{habit.duration} min</Text>
              </View>
            </View>
            <View style={styles.successRate}>
              <Text style={styles.successRateValue}>{habit.successRate}%</Text>
              <Text style={styles.successRateLabel}>Success Rate</Text>
            </View>
          </View>

          <Text style={styles.habitDescription}>{habit.description}</Text>

          <View style={styles.habitBenefit}>
            <Ionicons name='trending-up' size={16} color='#10B981' />
            <Text style={styles.benefitText}>{habit.expectedBenefit}</Text>
          </View>

          <View style={styles.difficultyIndicator}>
            <Text style={styles.difficultyLabel}>Difficulty:</Text>
            <View style={styles.difficultyDots}>
              {[1, 2, 3].map(dot => (
                <View
                  key={dot}
                  style={[
                    styles.difficultyDot,
                    {
                      backgroundColor:
                        dot <= getDifficultyLevel(habit.difficulty)
                          ? getDifficultyColor(habit.difficulty)
                          : '#E5E7EB',
                    },
                  ]}
                />
              ))}
            </View>
          </View>

          {habit.implementationTips.length > 0 && (
            <View style={styles.implementationTips}>
              <Text style={styles.tipsTitle}>Implementation Tips:</Text>
              {habit.implementationTips.slice(0, 2).map((tip, index) => (
                <Text key={index} style={styles.tip}>
                  • {tip}
                </Text>
              ))}
            </View>
          )}

          <View style={styles.trackingMethod}>
            <Ionicons name='analytics-outline' size={16} color='#6750A4' />
            <Text style={styles.trackingText}>
              Track via: {habit.trackingMethod}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );

  const renderSchedule = () => (
    <View style={styles.scheduleContainer}>
      <View style={styles.scheduleOverview}>
        <Text style={styles.cardTitle}>Schedule Optimization</Text>
        <View style={styles.scheduleMetrics}>
          <View style={styles.scheduleMetric}>
            <Text style={styles.scheduleMetricValue}>
              {optimization.scheduleOptimization.flexibilityScore}%
            </Text>
            <Text style={styles.scheduleMetricLabel}>Flexibility</Text>
          </View>
          <View style={styles.scheduleMetric}>
            <Text style={styles.scheduleMetricValue}>
              {optimization.scheduleOptimization.adherenceRate}%
            </Text>
            <Text style={styles.scheduleMetricLabel}>Adherence</Text>
          </View>
        </View>
      </View>

      <View style={styles.scheduleComparison}>
        <Text style={styles.sectionTitle}>Current vs Optimized Schedule</Text>

        <View style={styles.scheduleBlocks}>
          <Text style={styles.scheduleLabel}>Current Schedule</Text>
          {optimization.scheduleOptimization.currentSchedule.map(
            (block, index) => (
              <View key={index} style={styles.scheduleBlock}>
                <Text style={styles.blockTime}>
                  {block.startTime} - {block.endTime}
                </Text>
                <Text style={styles.blockActivity}>{block.activity}</Text>
                <View style={styles.blockMetrics}>
                  <View
                    style={[
                      styles.priorityIndicator,
                      { backgroundColor: getPriorityColor(block.priority) },
                    ]}
                  />
                  <Text style={styles.blockEffectiveness}>
                    {block.effectiveness}% effective
                  </Text>
                </View>
              </View>
            )
          )}
        </View>

        <View style={styles.scheduleBlocks}>
          <Text style={styles.scheduleLabel}>Optimized Schedule</Text>
          {optimization.scheduleOptimization.optimizedSchedule.map(
            (block, index) => (
              <View
                key={index}
                style={[styles.scheduleBlock, styles.optimizedBlock]}
              >
                <Text style={styles.blockTime}>
                  {block.startTime} - {block.endTime}
                </Text>
                <Text style={styles.blockActivity}>{block.activity}</Text>
                <View style={styles.blockMetrics}>
                  <View
                    style={[
                      styles.priorityIndicator,
                      { backgroundColor: getPriorityColor(block.priority) },
                    ]}
                  />
                  <Text style={styles.blockEffectiveness}>
                    {block.effectiveness}% effective
                  </Text>
                </View>
              </View>
            )
          )}
        </View>
      </View>

      <View style={styles.improvements}>
        <Text style={styles.sectionTitle}>Suggested Improvements</Text>
        {optimization.scheduleOptimization.improvements.map(
          (improvement, index) => (
            <View key={index} style={styles.improvementCard}>
              <Text style={styles.improvementTitle}>
                {improvement.improvement}
              </Text>
              <Text style={styles.improvementRationale}>
                {improvement.rationale}
              </Text>
              <Text style={styles.improvementBenefit}>
                Expected: {improvement.expectedBenefit}
              </Text>
              <View style={styles.improvementDifficulty}>
                <Text style={styles.difficultyLabel}>Implementation:</Text>
                <Text
                  style={[
                    styles.difficultyValue,
                    {
                      color: getDifficultyColor(
                        improvement.implementationDifficulty
                      ),
                    },
                  ]}
                >
                  {improvement.implementationDifficulty}
                </Text>
              </View>
            </View>
          )
        )}
      </View>
    </View>
  );

  const renderOptimizationPlan = () => (
    <View style={styles.planContainer}>
      <View style={styles.planOverview}>
        <Text style={styles.cardTitle}>Optimization Plan</Text>
        <Text style={styles.planTimeframe}>
          {optimization.optimizationPlan.timeframe} day implementation plan
        </Text>
        <Text style={styles.planCreated}>
          Created:{' '}
          {new Date(
            optimization.optimizationPlan.createdAt
          ).toLocaleDateString()}
        </Text>
      </View>

      <View style={styles.phases}>
        <Text style={styles.sectionTitle}>Implementation Phases</Text>
        {optimization.optimizationPlan.phases.map(phase => (
          <View key={phase.phaseNumber} style={styles.phaseCard}>
            <View style={styles.phaseHeader}>
              <View style={styles.phaseNumber}>
                <Text style={styles.phaseNumberText}>{phase.phaseNumber}</Text>
              </View>
              <View style={styles.phaseInfo}>
                <Text style={styles.phaseName}>{phase.name}</Text>
                <Text style={styles.phaseDuration}>{phase.duration} days</Text>
              </View>
            </View>

            <View style={styles.phaseObjectives}>
              <Text style={styles.objectivesTitle}>Objectives:</Text>
              {phase.objectives.map((objective, index) => (
                <Text key={index} style={styles.objective}>
                  • {objective}
                </Text>
              ))}
            </View>

            <View style={styles.phaseActivities}>
              <Text style={styles.activitiesTitle}>Activities:</Text>
              {phase.activities.map((activity, index) => (
                <Text key={index} style={styles.activity}>
                  • {activity}
                </Text>
              ))}
            </View>
          </View>
        ))}
      </View>

      <View style={styles.expectedOutcomes}>
        <Text style={styles.sectionTitle}>Expected Outcomes</Text>
        {optimization.optimizationPlan.expectedOutcomes.map(
          (outcome, index) => (
            <View key={index} style={styles.outcomeCard}>
              <View style={styles.outcomeHeader}>
                <Text style={styles.outcomeName}>{outcome.outcome}</Text>
                <Text style={styles.outcomeProbability}>
                  {outcome.probability}%
                </Text>
              </View>
              <Text style={styles.outcomeTimeframe}>
                Expected in {outcome.timeframe} days
              </Text>
              <Text style={styles.outcomeMetric}>
                Target: {outcome.targetValue} {outcome.measurableMetric}
              </Text>
            </View>
          )
        )}
      </View>
    </View>
  );

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return '#10B981';
      case 'moderate':
        return '#F59E0B';
      case 'challenging':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const getDifficultyLevel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 1;
      case 'moderate':
        return 2;
      case 'challenging':
        return 3;
      default:
        return 1;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'learning':
        return '#6366F1';
      case 'practice':
        return '#10B981';
      case 'review':
        return '#F59E0B';
      case 'reflection':
        return '#8B5CF6';
      case 'rest':
        return '#06B6D4';
      default:
        return '#6B7280';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'opportunities':
        return renderOpportunities();
      case 'habits':
        return renderHabits();
      case 'schedule':
        return renderSchedule();
      case 'plan':
        return renderOptimizationPlan();
      default:
        return renderOverview();
    }
  };

  return (
    <View style={styles.container}>
      {renderTabBar()}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderContent()}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  tabBar: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 8,
  },
  tabScrollContent: {
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
  },
  activeTab: {
    backgroundColor: '#EDE7F6',
  },
  tabText: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: '#6750A4',
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  overviewContainer: {
    gap: 16,
  },
  efficiencyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  efficiencyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  potentialGain: {
    alignItems: 'center',
  },
  potentialGainValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#10B981',
  },
  potentialGainLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  efficiencyComparison: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  efficiencyCircleContainer: {
    position: 'relative',
  },
  efficiencyCenter: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  efficiencyValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2937',
  },
  efficiencyLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  learningStyleCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  learningStyleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  learningStyleInfo: {
    flex: 1,
  },
  primaryStyle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  secondaryStyle: {
    fontSize: 14,
    color: '#6B7280',
  },
  alignmentScore: {
    alignItems: 'center',
  },
  alignmentValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#6750A4',
  },
  alignmentLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  alignmentBar: {
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 12,
  },
  alignmentFill: {
    height: '100%',
    backgroundColor: '#6750A4',
    borderRadius: 4,
  },
  contentAlignment: {
    fontSize: 14,
    color: '#6B7280',
  },
  quickStats: {
    flexDirection: 'row',
    gap: 12,
  },
  statCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginTop: 8,
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
    textAlign: 'center',
  },
  opportunitiesContainer: {
    gap: 16,
  },
  opportunityCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  opportunityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  opportunityArea: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  potentialGainBadge: {
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  potentialGainText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#16A34A',
  },
  opportunityStates: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  stateContainer: {
    flex: 1,
  },
  stateLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  stateValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
  },
  opportunityMetrics: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  metric: {
    alignItems: 'center',
  },
  metricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  metricValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  resources: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
  },
  resourcesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  resource: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  habitsContainer: {
    gap: 16,
  },
  habitCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  habitHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  habitInfo: {
    flex: 1,
  },
  habitName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 6,
  },
  habitMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  categoryBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  habitFrequency: {
    fontSize: 12,
    color: '#6B7280',
  },
  habitDuration: {
    fontSize: 12,
    color: '#6B7280',
  },
  successRate: {
    alignItems: 'center',
  },
  successRateValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#10B981',
  },
  successRateLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  habitDescription: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 18,
    marginBottom: 12,
  },
  habitBenefit: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0FDF4',
    borderRadius: 8,
    padding: 8,
    marginBottom: 12,
    gap: 6,
  },
  benefitText: {
    fontSize: 12,
    color: '#15803D',
    flex: 1,
  },
  difficultyIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  difficultyLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  difficultyDots: {
    flexDirection: 'row',
    gap: 4,
  },
  difficultyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  implementationTips: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  tipsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  tip: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  trackingMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  trackingText: {
    fontSize: 12,
    color: '#6750A4',
    fontWeight: '500',
  },
  scheduleContainer: {
    gap: 20,
  },
  scheduleOverview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  scheduleMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 16,
  },
  scheduleMetric: {
    alignItems: 'center',
  },
  scheduleMetricValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#6750A4',
  },
  scheduleMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  scheduleComparison: {
    gap: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  scheduleBlocks: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  scheduleLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  scheduleBlock: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  optimizedBlock: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  blockTime: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6750A4',
    marginBottom: 4,
  },
  blockActivity: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 6,
  },
  blockMetrics: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  priorityIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  blockEffectiveness: {
    fontSize: 12,
    color: '#6B7280',
  },
  improvements: {
    gap: 12,
  },
  improvementCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  improvementTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 6,
  },
  improvementRationale: {
    fontSize: 12,
    color: '#4B5563',
    marginBottom: 4,
    lineHeight: 16,
  },
  improvementBenefit: {
    fontSize: 12,
    color: '#10B981',
    fontWeight: '500',
    marginBottom: 8,
  },
  improvementDifficulty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  difficultyValue: {
    fontSize: 12,
    fontWeight: '500',
  },
  planContainer: {
    gap: 20,
  },
  planOverview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  planTimeframe: {
    fontSize: 14,
    color: '#6750A4',
    fontWeight: '500',
    marginTop: 8,
    marginBottom: 4,
  },
  planCreated: {
    fontSize: 12,
    color: '#6B7280',
  },
  phases: {
    gap: 12,
  },
  phaseCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  phaseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  phaseNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#6750A4',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  phaseNumberText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  phaseInfo: {
    flex: 1,
  },
  phaseName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  phaseDuration: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  phaseObjectives: {
    marginBottom: 12,
  },
  objectivesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  objective: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  phaseActivities: {
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    padding: 8,
  },
  activitiesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  activity: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  expectedOutcomes: {
    gap: 12,
  },
  outcomeCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  outcomeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  outcomeName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  outcomeProbability: {
    fontSize: 14,
    fontWeight: '600',
    color: '#10B981',
  },
  outcomeTimeframe: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  outcomeMetric: {
    fontSize: 12,
    color: '#6750A4',
    fontWeight: '500',
  },
});
